import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useComprehensiveTopNav from 'hooks/useComprehensiveTopNav';
import { useWindowResize } from 'hooks/useWindowResize';
import { Typography, Image } from 'lib/components';
import { AdminTypeSelect, TextField } from 'lib/components/FormFields';
import PermissionRow from './PermissionRow';
import { useAppDispatch, actions, useSelectRequest, RequestManager } from 'store';
import routes from 'lib/api/routes';
import useCustomToast from 'hooks/toast';
import { Permission, PermissionType, PERMISSIONS, Timing } from 'lib/util';
import { AllCheckedStatus } from 'types';
import classes from './index.module.scss';

/**
 * AdminCreate component
 * Form for creating a new administrator account
 * Allows setting basic info, admin type, and permissions
 *
 * @returns {JSX.Element} - Rendered component
 */
const AdminCreate = () => {
	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Navigation helper
	const { push } = useHistory();
	// Responsive design helpers
	const { isSmallerDevice } = useWindowResize();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();

	// Form state variables
	/** Admin name input value */
	const [name, setName] = useState('');
	/** Admin type selection (1 = Super Admin, 2 = Admin) */
	const [adminType, setAdminType] = useState<number | undefined>();
	/** Admin email input value */
	const [email, setEmail] = useState('');
	/** Admin phone number input value */
	const [phoneNumber, setPhoneNumber] = useState('');
	/** Admin password input value */
	const [password, setPassword] = useState('');
	/** Selected permissions for the admin */
	const [permissions, setPermissions] = useState<PermissionType[]>([]);
	/** Overall check status for permissions ('all', 'none', or 'some') */
	const [allCheckedStatus, setAllCheckedStatus] = useState<AllCheckedStatus>('none');

	/**
	 * Monitor API requests for admin creation
	 * Shows toast notifications for success/failure and redirects on success
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.ADMIN, 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				push('/admins');
			}
		}
	}, [active]);

	/**
	 * Reset permissions when admin type changes to Super Admin (type 1)
	 * Super Admins have all permissions by default, so individual selections are not needed
	 */
	useEffect(() => {
		if (adminType === 1) {
			setAllCheckedStatus('none');
			setPermissions([]);
		}
	}, [adminType]);

	/**
	 * Handle permission checkbox changes
	 * Manages dependencies between permissions (e.g., adding read permissions when needed)
	 *
	 * @param {PermissionType} id - The permission being toggled
	 * @param {boolean} isChecked - Whether the permission is being checked or unchecked
	 */
	const handleCheckAction = (id: PermissionType, isChecked: boolean) => {
		// Set overall status to 'some' since we're making individual selections
		setAllCheckedStatus('some');

		if (isChecked && !permissions.includes(id)) {
			// When checking a permission, also add any required dependent permissions
			setPermissions((prev) => [id, ...Permission.addListAndReadPermissions(prev, id)]);
			return;
		}

		if (!isChecked && permissions.includes(id)) {
			// When unchecking a permission, also remove any dependent permissions
			setPermissions((prev) => [...Permission.removeCreateAndUpdatePermissions(prev, id)]);
		}
	};

	/**
	 * Handle form submission to create a new admin
	 * Dispatches the addAdmin action with form data
	 */
	const handleCreate = () => {
		dispatch(
			actions.addAdmin({
				name,
				// Default to regular Admin (type 2) if no selection made
				roleId: adminType ? adminType : 2,
				permissions,
				email,
				// Use current date as default date of birth
				dateOfBirth: Timing.formatDate(new Date()),
				// Only include phone number if provided
				phoneNumber: phoneNumber !== '' ? phoneNumber : undefined,
				password,
			})
		);
	};

	// Set up the top navigation bar with title
	useComprehensiveTopNav('Create a new Admin Profile');

	return (
		<div
			className={classes['profile-container']}
			style={{
				flexDirection: 'column',
			}}
		>
			<>
				{/* Profile header section with avatar and name */}
				<div className={classes['top-container']}>
					{/* Avatar image area */}
					<div className={classes['image-area']}>
						<div
							className={classes['profile-image-container']}
							style={{
								margin: '2rem',
								border: 'none',
							}}
						>
							<Image src={'plAvatar'} style={{ width: '100%', height: '100%' }} />
						</div>
					</div>
					{/* Basic profile details */}
					<div className={classes['profile-details']}>
						{/* Name input field */}
						<div style={{ margin: '0rem 1rem 1rem 0' }}>
							<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
								Name:
							</Typography>
							<input
								className={classes['input']}
								style={{
									width: '15rem',
									minWidth: 250,
								}}
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</div>
						{/* Admin type selection dropdown */}
						<div style={{ margin: '0rem 1rem 1rem 0' }}>
							<AdminTypeSelect setValue={setAdminType} />
						</div>
					</div>
				</div>

				{/* Contact information section */}
				<div className={classes['section']}>
					{/* Basic contact info fields */}
					<div className={classes['section-group']}>
						<Typography weight="bold">Basic info:</Typography>
						<div className={classes['section-groupFields']}>
							{/* Phone number input */}
							<TextField
								placeholder="+1 75 85 41 223"
								label="Mobile Number:"
								variant="labelled"
								value={phoneNumber}
								onChange={(e) => setPhoneNumber(e.target.value)}
							/>
							{/* Email input */}
							<TextField
								placeholder="reginasmith@spencerhospital.com"
								label="Email:"
								variant="labelled"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
						</div>
					</div>
					{/* Password section */}
					<div className={classes['section-group']}>
						<Typography weight="bold">Set New Password:</Typography>
						<div className={classes['section-groupFields']}>
							{/* Password input field */}
							<TextField
								placeholder="*****************"
								label="Password:"
								variant="labelled"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
						</div>
					</div>
				</div>

				{/* Permissions section - only shown for regular Admin (type 2) */}
				{adminType === 2 && (
					<div className={classes['permissions']}>
						<Typography style={{ paddingLeft: '2rem' }} weight="bold">
							Edit Roles Permissions
						</Typography>
						{/* Permissions table */}
						<div className={classes['permissions-table']}>
							{/* Table header row */}
							<div className={classes['permissions-tableRow']}>
								<div className={classes['permissions-tableCol']}>
									<Typography weight="bold">Role</Typography>
								</div>
								<div className={classes['permissions-tableCol']}>
									<Typography weight="bold">Permissions</Typography>
								</div>
								<div className={classes['permissions-tableCol']} />
							</div>
							{/* Map through all available permissions and render a row for each */}
							{PERMISSIONS.map((permission) => (
								<PermissionRow
									key={permission}
									permission={permission}
									list={permissions}
									status={allCheckedStatus}
									checkAction={handleCheckAction}
								/>
							))}
						</div>
					</div>
				)}

				{/* Form action buttons */}
				<div
					className={classes['row']}
					style={{
						margin: 'auto 0 1rem 1rem',
					}}
				>
					{/* Create button */}
					<button
						className={classes['button']}
						style={{
							width: 'auto',
							marginRight: '1rem',
						}}
						onClick={handleCreate}
					>
						<p>Create New Profile</p>
					</button>
					{/* Cancel button - returns to admin list */}
					<button
						className={`${classes['button']} ${classes['palewhite']}`}
						style={{
							width: 'auto',
						}}
						onClick={() => push('/admins')}
					>
						<p>Cancel</p>
					</button>
				</div>
			</>
		</div>
	);
};

export default AdminCreate;
